<script setup lang="ts">
import { useSocialStore } from '@/stores/social';
import { useFooterStore } from '@/stores/footer';
const inboundLogo = '/img/2026/footer/inbound-logo.svg';
const hubspotLogo = '/img/2026/footer/hubspot-logo.svg';
const socialLinkedin = '/img/2026/footer/social-linkedin.svg';
const socialInstagram = '/img/2026/footer/social-instagram.svg';
const socialTiktok = '/img/2026/footer/social-tiktok.svg';
const socialFacebook = '/img/2026/footer/social-facebook.svg';
const socialYoutube = '/img/2026/footer/social-youtube.svg';
const socialX = '/img/2026/footer/social-x.svg';
const arrowSvg = '/img/2026/footer/arrow.svg';
const privacyBug = '/img/2026/footer/privacy-bug.png';

const currentYear = useState('currentYear', () => new Date().getFullYear());
const socialStore = useSocialStore();
const footerStore = useFooterStore();
const { footerLinks, newsletter } = toRefs(footerStore);

const config = useRuntimeConfig();
const baseURL = config.public.clientFrontEnd;
const route = useRoute();

const hubspotPortalId = '146726';
const hubspotFormId = 'bbc39d17-c359-4890-b3ba-c487a27ed366';
const hubspotCookie = useCookie('hubspotutk');

const emailValue = ref('');
const honeypotValue = ref('');
const hasError = ref(false);
const submitted = ref(false);
const submittedMessage = ref('');

const submit = async () => {
    if (!emailValue.value) {
        hasError.value = true;
        return;
    }
    hasError.value = false;
    const { data: responseData } = await useFetch(`https://api.hsforms.com/submissions/v3/integration/submit/${hubspotPortalId}/${hubspotFormId}`, {
        method: 'post',
        body: {
            fields: [
                {
                    objectTypeId: '0-1',
                    name: 'email',
                    value: emailValue.value,
                },
                {
                    objectTypeId: '0-1',
                    name: 'special_email_capture',
                    value: honeypotValue.value,
                },
            ],
            context: {
                hutk: hubspotCookie.value,
                pageUri: `${baseURL}${route.fullPath}`,
                pageName: `UNBOUND 27 - Footer`,
            },
        },
    });
    submittedMessage.value = responseData.value?.inlineMessage || 'Thanks for signing up!';
    submitted.value = true;
    setTimeout(() => {
        submitted.value = false;
    }, 10000);
};

const newsletterHeadline = computed(() => newsletter.value?.headline || 'Get first access to news and updates:');
const newsletterSubText = computed(() => newsletter.value?.subText || '');
const newsletterButtonText = computed(() => newsletter.value?.buttonText || 'Submit email');

// Map social icons by platform name
const socialIconMap: Record<string, string> = {
    linkedin: socialLinkedin,
    instagram: socialInstagram,
    tiktok: socialTiktok,
    facebook: socialFacebook,
    youtube: socialYoutube,
    x: socialX,
    twitter: socialX,
};

const defaultSocials = [
    { icon: socialLinkedin, label: 'LinkedIn', url: 'https://www.linkedin.com/showcase/inbound/' },
    { icon: socialInstagram, label: 'Instagram', url: 'https://www.instagram.com/hubspot/' },
    { icon: socialTiktok, label: 'TikTok', url: 'https://www.tiktok.com/@hubspot' },
    { icon: socialFacebook, label: 'Facebook', url: 'https://www.facebook.com/HubSpot/' },
    { icon: socialYoutube, label: 'YouTube', url: 'https://www.youtube.com/user/HubSpot' },
    { icon: socialX, label: 'X (Twitter)', url: 'https://x.com/INBOUND' },
];

const socials = computed(() => {
    const storeSocials = socialStore.socials?.socials;
    if (storeSocials?.length) {
        return storeSocials
            .filter((s: any) => s.url)
            .map((s: any) => ({
                icon: socialIconMap[s.type?.toLowerCase()] || socialX,
                label: s.type || '',
                url: s.url,
            }));
    }
    return defaultSocials;
});

async function manageCookies() {
    const _hsp = ((window as any)._hsp = (window as any)._hsp || []);
    _hsp.push(['showBanner']);
}
</script>

<template>
    <footer class="block-footer relative overflow-hidden
        pb-[28px] pt-[36px] px-[var(--box-padding-lr-m)]
        lg:pb-[30px] lg:pt-[56px] lg:px-[40px]">

        <!-- Orange gradient glow — bottom right -->
        <img src="/img/2026/footer-mobile-bottom-circle.png" alt="" aria-hidden="true"
            class="lg:hidden absolute bottom-0 right-0 z-0 pointer-events-none" />
        <img src="/img/2026/footer-desktop-bottom-circle.png" alt="" aria-hidden="true"
            class="hidden lg:display-block absolute bottom-0 right-0 z-0 pointer-events-none" />

        <div class="relative z-[1] flex flex-col gap-[28px] lg:gap-[65px]">
            <!-- Top row -->
            <div class="flex flex-col gap-[31px]
                lg:flex-row lg:items-start lg:gap-[67px] xl:gap-[134px]">

                <!-- Left column: logos + social icons -->
                <div class="flex flex-col-reverse gap-[31px] pt-0
                    order-2 lg:order-1
                    lg:flex-col lg:gap-[47px] lg:pt-[10px] lg:w-[327px] lg:shrink-0">

                    <!-- Logos -->
                    <div class="flex gap-[24px] items-center">
                        <img :src="inboundLogo" loading="lazy" decoding="async" alt="UNBOUND 27"
                            class="h-[28px] w-[102px] lg:h-[34px] lg:w-[123px]" />
                        <img :src="hubspotLogo" loading="lazy" decoding="async" alt="HubSpot"
                            class="h-[23px] w-[82px] lg:h-[28px] lg:w-[100px]" />
                    </div>

                    <!-- Social icons -->
                    <nav aria-label="Social media links" class="flex gap-[15px] items-center">
                        <a v-for="social in socials" :key="social.label"
                            :href="social.url"
                            :aria-label="social.label"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="social-icon display-block size-[42px] shrink-0
                                transition-all duration-200
                                focus-visible:outline-none focus-visible:ring-2
                                focus-visible:ring-[var(--pink)] focus-visible:rounded-full">
                            <img :src="social.icon" loading="lazy" decoding="async" :alt="''" aria-hidden="true"
                                class="size-full" />
                        </a>
                    </nav>
                </div>

                <!-- Right column: email signup + disclaimer -->
                <div class="flex flex-col gap-[18px]
                    order-1 lg:order-2
                    lg:flex-1 lg:min-w-0">

                    <!-- Email signup row -->
                    <div v-if="!submitted" class="flex flex-col gap-[17px]
                        lg:flex-row lg:items-center lg:gap-[20px]">
                        <p class="font-hubspot-serif font-normal
                            text-[20px] leading-[1.4] tracking-[0.4px]
                            text-white
                            lg:flex-1 lg:basis-0 lg:min-w-0">
                            {{ newsletterHeadline }}
                        </p>

                        <!-- Email input -->
                        <form class="relative flex items-center justify-between
                            h-[53px] w-full px-[24px] py-[10px]
                            border border-[var(--pink)] rounded-[50px]
                            transition-colors duration-200 focus-within:border-white
                            lg:flex-1 lg:basis-0 lg:min-w-0"
                            @submit.prevent="submit">
                            <label for="footer-email" class="sr-only">Email address</label>
                            <!-- Honeypot — hidden from real users, bots will fill it. HubSpot uses
                                 special_email_capture to mark these submissions as spam. -->
                            <div aria-hidden="true" class="absolute left-[-9999px] top-[-9999px] w-[1px] h-[1px] overflow-hidden">
                                <label for="footer-email-confirm">Don't fill this out if you're human</label>
                                <input id="footer-email-confirm" type="text" name="special_email_capture"
                                    tabindex="-1" autocomplete="off" v-model="honeypotValue" />
                            </div>
                            <input
                                id="footer-email"
                                type="email"
                                v-model="emailValue"
                                placeholder="Enter your email address"
                                class="bg-transparent border-none outline-none w-full
                                    font-hubspot-sans font-light
                                    text-[17px] leading-[1.4] tracking-[0.34px]
                                    text-[var(--pink)] placeholder:text-[var(--pink)]
                                    transition-colors duration-200 focus:text-white" />
                            <button type="submit" :aria-label="newsletterButtonText"
                                class="group shrink-0 ml-[10px]
                                    focus-visible:outline-none focus-visible:ring-2
                                    focus-visible:ring-[var(--pink)] focus-visible:rounded-full">
                                <img :src="arrowSvg" loading="lazy" decoding="async" alt="" aria-hidden="true"
                                    class="size-[13px] -scale-x-100 transition-[filter] duration-200 group-hover:brightness-[3]" />
                            </button>
                        </form>
                    </div>
                    <!-- Submitted confirmation -->
                    <div v-else class="font-hubspot-serif font-normal
                        text-[calc(21/var(--base-fs)*1rem)] leading-[1.5] tracking-[0.42px]
                        text-white" v-html="submittedMessage" />

                    <!-- Validation error -->
                    <p v-if="hasError" class="font-hubspot-sans font-light text-[calc(13/var(--base-fs)*1rem)] text-white">
                        *Please enter a valid email address.
                    </p>

                    <!-- Privacy disclaimer -->
                    <div v-if="newsletterSubText" class="newsletter-legal font-hubspot-sans font-light
                        text-[11px] leading-[18px] tracking-[0.22px]
                        text-[var(--pink)]"
                        v-html="newsletterSubText" />
                    <p v-else class="font-hubspot-sans font-light
                        text-[calc(11/var(--base-fs)*1rem)] leading-[18px] tracking-[0.22px]
                        text-[var(--pink)]">
                        We're committed to your privacy. INBOUND uses the information you
                        provide to us to contact you about INBOUND content and events. You
                        may unsubscribe from these communications at any time. For more
                        information, check out our
                        <a href="/privacy-policy"
                            class="text-[var(--pink)]
                                focus-visible:outline-none focus-visible:ring-2
                                focus-visible:ring-[var(--pink)]">privacy policy</a>.
                    </p>
                </div>
            </div>

            <!-- Bottom row: copyright + legal links -->
            <nav aria-label="Legal" class="flex flex-wrap gap-[18px] items-center
                font-hubspot-sans font-light
                text-[calc(11/var(--base-fs)*1rem)] leading-[18px] tracking-[0.22px]
                text-[var(--pink)]">
                <span>&copy; 2026 HubSpot, Inc.</span>
                <ClientOnly>
                    <ul class="hs-footer-links-wrapper portal-links flex flex-wrap gap-[18px] items-center">
                        <li class="hs-footer-cookie-settings">
                            <a href="#" aria-label="Open Cookie Settings Dialog" @click.prevent="manageCookies">Manage Cookies</a>
                        </li>
                        <li v-for="(footerLink, index) in footerLinks" :key="index">
                            <MessLink :href="footerLink.url" :label="footerLink.label">{{ footerLink.label }}</MessLink>
                        </li>
                    </ul>
                </ClientOnly>
            </nav>
        </div>
    </footer>
</template>

<style>
.block-footer {
    background: linear-gradient(319deg, #5C0859 25.36%, var(--magenta) 67.13%), #fff;
}

.block-footer .social-icon:hover {
    background: white;
    border-radius: 50%;
}

.block-footer .social-icon:hover img {
    filter: brightness(0) saturate(100%) invert(5%) sepia(30%) saturate(5000%) hue-rotate(300deg);
}

.block-footer a {
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 3px;
    transition: color 0.2s, text-decoration 0.2s;
}

.block-footer a:hover {
    color: white;
    text-decoration: none;
}

/* Force newsletter legal text children to inherit parent size */
.block-footer .newsletter-legal * {
    font-size: inherit !important;
    line-height: inherit !important;
    letter-spacing: inherit !important;
}

/* Newsletter legal links — dotted pink underline per Figma */
.block-footer .newsletter-legal a {
    color: inherit;
    text-decoration: underline dotted currentColor;
    text-decoration-thickness: 8.5%;
    text-underline-offset: 2px;
}

.block-footer .newsletter-legal a:hover {
    color: white;
    text-decoration: none;
}
</style>
